import { signL1Action } from '@nktkas/hyperliquid/signing';
import type { Hip3Market } from '../types';
import type { WalletClient } from 'viem';

const EXCHANGE_API = 'https://api.hyperliquid.xyz/exchange';
const TESTNET_EXCHANGE_API = 'https://api.hyperliquid-testnet.xyz/exchange';

// Adapter to make WalletClient compatible with @nktkas/hyperliquid's AbstractWallet
function createWalletAdapter(walletClient: WalletClient) {
  return {
    async signTypedData(params: {
      domain: { name: string; version: string; chainId: number; verifyingContract: `0x${string}` };
      types: { [key: string]: { name: string; type: string }[] };
      primaryType: string;
      message: Record<string, unknown>;
    }): Promise<`0x${string}`> {
      const [account] = await walletClient.getAddresses();
      if (!account) {
        throw new Error('No account connected');
      }
      return walletClient.signTypedData({
        account,
        domain: params.domain,
        types: params.types,
        primaryType: params.primaryType,
        message: params.message,
      });
    },
    async getAddresses(): Promise<`0x${string}`[]> {
      return walletClient.getAddresses();
    },
    async getChainId(): Promise<number> {
      return walletClient.getChainId();
    },
  };
}

interface OrderAction {
  type: 'order';
  orders: Array<{
    a: number; // asset index
    b: boolean; // is buy
    p: string; // price
    s: string; // size
    r: boolean; // reduce only
    t: { limit: { tif: 'Alo' | 'Ioc' | 'Gtc' } } | { trigger: { isMarket: boolean; triggerPx: string; tpsl: 'tp' | 'sl' } };
    c?: string; // client order ID (optional)
  }>;
  grouping: 'na' | 'normalTpsl' | 'positionTpsl';
  builder?: {
    b: string; // builder address
    f: number; // fee in tenths of basis point (10 = 1 bp = 0.01%)
  };
}

interface OrderResponse {
  status: 'ok' | 'err';
  response: {
    type: 'order';
    data: {
      statuses: Array<
        | { resting: { oid: number } }
        | { filled: { totalSz: string; avgPx: string; oid: number } }
        | { error: string }
      >;
    };
  };
}

export interface OrderExecutionParams {
  market: Hip3Market;
  side: 'buy' | 'sell';
  size: number;
  type: 'market' | 'limit';
  price?: number;
  reduceOnly?: boolean;
  walletClient: WalletClient;
  builderAddress?: string; // Builder code address
  builderFeeBps?: number; // Builder fee in basis points (e.g., 1 = 0.01%)
  useTestnet?: boolean;
}

export interface OrderResult {
  success: boolean;
  orderId?: number;
  filledSize?: string;
  avgPrice?: string;
  error?: string;
  rawResponse?: OrderResponse;
}

// Asset index cache: maps symbol -> { index, dex }
// For native markets: dex is undefined
// For HIP-3 markets: dex is the DEX name (e.g., "xyz")
interface AssetIndexEntry {
  index: number;
  dex?: string;
}
let assetIndexCache: Map<string, AssetIndexEntry> | null = null;

// Get asset index and DEX from market symbol
// The index corresponds to the position in the meta universe array for that DEX
async function getAssetIndex(marketSymbol: string): Promise<AssetIndexEntry> {
  // Build cache if not exists - discover all DEXes
  if (!assetIndexCache) {
    assetIndexCache = new Map();
    try {
      // Step 1: Discover all perpetual DEXes
      const dexsResponse = await fetch('https://api.hyperliquid.xyz/info', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'perpDexs' }),
      });
      const dexs: Array<null | { name: string }> = await dexsResponse.json();

      console.log('[Order] Discovered DEXs:', dexs.length);

      // Step 2: Fetch metaAndAssetCtxs for each DEX
      for (let i = 0; i < dexs.length; i++) {
        const dex = dexs[i];
        const isNative = i === 0; // First element (null) is native Hyperliquid
        const dexName = isNative ? undefined : dex?.name;

        try {
          const request: any = { type: 'metaAndAssetCtxs' };
          if (dexName) {
            request.dex = dexName;
          }

          const response = await fetch('https://api.hyperliquid.xyz/info', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(request),
          });
          const [meta] = await response.json();

          meta.universe.forEach((asset: any, index: number) => {
            // For native DEX: asset.name = "BTC", "ETH"
            // For HIP-3 DEX: asset.name = "xyz:GOLD" (already prefixed)
            assetIndexCache!.set(asset.name, { index, dex: dexName });
          });

          console.log(`[Order] Cached ${meta.universe.length} assets for ${isNative ? 'native' : dexName}`);
        } catch (error) {
          console.error(`[Order] Failed to fetch assets for DEX ${dexName || 'native'}:`, error);
        }
      }
    } catch (error) {
      console.error('[Order] Failed to discover DEXes:', error);
    }
  }

  // Look up the index
  const entry = assetIndexCache.get(marketSymbol);
  if (entry !== undefined) {
    console.log(`[Order] Found asset ${marketSymbol}: index=${entry.index}, dex=${entry.dex || 'native'}`);
    return entry;
  }

  // Debug: log what we have in cache
  console.log('[Order] Asset not found. Cache contains:', Array.from(assetIndexCache.keys()).slice(0, 20));

  throw new Error(`Asset not found for ${marketSymbol}`);
}

// Remove trailing zeros from price/size strings
function cleanNumber(value: string): string {
  // Remove trailing zeros after decimal point
  // "50000.0000" -> "50000"
  // "0.1000" -> "0.1"
  return parseFloat(value).toString();
}

export async function executeOrder(params: OrderExecutionParams): Promise<OrderResult> {
  const {
    market,
    side,
    size,
    type,
    price,
    reduceOnly = false,
    walletClient,
    builderAddress,
    builderFeeBps,
    useTestnet = false,
  } = params;

  try {
    // Get asset index and DEX info
    const assetInfo = await getAssetIndex(market.symbol);
    const assetIndex = assetInfo.index;

    console.log(`[Order] Placing order on ${market.symbol}: asset=${assetIndex}, dex=${assetInfo.dex || 'native'}`);

    // Prepare order based on type
    const orderPrice = type === 'market' ? '0' : cleanNumber(price!.toString());
    const orderSize = cleanNumber(size.toString());

    // Validate minimum order value ($10)
    const estimatedValue = type === 'market' ? size * 50000 : size * price!; // rough estimate
    if (estimatedValue < 10) {
      return {
        success: false,
        error: 'Order must have minimum value of $10',
      };
    }

    // Build order action
    const orderAction: OrderAction = {
      type: 'order',
      orders: [
        {
          a: assetIndex,
          b: side === 'buy',
          p: orderPrice,
          s: orderSize,
          r: reduceOnly,
          t:
            type === 'market'
              ? { limit: { tif: 'Ioc' } } // Market orders use IOC (Immediate or Cancel)
              : { limit: { tif: 'Gtc' } }, // Limit orders use GTC (Good til Cancel)
        },
      ],
      grouping: 'na',
    };

    // Add builder code if provided
    if (builderAddress && builderFeeBps) {
      orderAction.builder = {
        b: builderAddress,
        f: builderFeeBps * 10, // Convert basis points to tenths of basis point (1 bp = 10 units)
      };
    }

    // Generate nonce
    const nonce = Date.now();

    // Create wallet adapter and sign using @nktkas/hyperliquid library
    const wallet = createWalletAdapter(walletClient);
    const signature = await signL1Action({
      wallet,
      action: orderAction as unknown as Record<string, unknown>,
      nonce,
      isTestnet: useTestnet,
    });

    // Execute the order
    const apiUrl = useTestnet ? TESTNET_EXCHANGE_API : EXCHANGE_API;
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: orderAction,
        nonce,
        signature,
      }),
    });

    if (!response.ok) {
      return {
        success: false,
        error: `API error: ${response.statusText}`,
      };
    }

    const result: OrderResponse = await response.json();

    // Handle response
    if (result.status !== 'ok') {
      return {
        success: false,
        error: 'Order execution failed',
        rawResponse: result,
      };
    }

    const orderStatus = result.response.data.statuses[0];

    if ('error' in orderStatus) {
      return {
        success: false,
        error: orderStatus.error,
        rawResponse: result,
      };
    }

    if ('filled' in orderStatus) {
      return {
        success: true,
        orderId: orderStatus.filled.oid,
        filledSize: orderStatus.filled.totalSz,
        avgPrice: orderStatus.filled.avgPx,
        rawResponse: result,
      };
    }

    if ('resting' in orderStatus) {
      return {
        success: true,
        orderId: orderStatus.resting.oid,
        rawResponse: result,
      };
    }

    return {
      success: false,
      error: 'Unknown order status',
      rawResponse: result,
    };
  } catch (error) {
    console.error('Order execution error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
}
